/*
  Kiwi Ears · sound-profile-icon — stylised tonal-balance graphs.

  Vendored from Rene's single-file demo (2026-08-21) and adapted to the theme:
    · fonts come from --font-sans, not Inter
    · `decorative` suppresses the focus/click behaviour (for icons that sit
      inside a button, where a focusable child would be a trap) WITHOUT changing
      the drawing — the graph still renders exactly as the demo does
    · `compact` renders the plot only, on a tight viewBox, for slots too small
      for the band labels and axis captions. Prefer `decorative` and full size:
      the labels, axes and markers are the icon.
    · the point fill follows the surface it sits on (--sp-point-fill)
  Styles live in src/styles/main.css, per CLAUDE.md — no new global CSS files.

  These are ILLUSTRATIONS of tonal balance, not measured product response. The
  surfaces that use them say so; keep that caption if you move them.

  Usage:  <sound-profile-icon profile="warm"></sound-profile-icon>
          <sound-profile-icon profile="warm" decorative>   <!-- inside a button -->
          <sound-profile-icon profile="warm" compact>      <!-- tiny slots only -->
  Profiles: warm · balanced · vocal · powerful · spacious

  CURVE DATA (Rene 2026-09-15) — the same source as the large graph in
  assets/kiwi-profile-graph.js: the filter chains that DEFINE these profiles in
  the Linsoul sound-profile references, evaluated with the RBJ biquad model
  those pages use (W3C Audio EQ Cookbook, 96 kHz), 48 log-spaced points.

  The two graphs show that one source in the two ways the package itself
  supports. The large chart plots the "response" curve — the profile applied
  over the Harman in-ear 2019 reference — because a full-size chart can carry a
  40 dB span and a real in-ear shape. This icon plots the "relative" curve —
  the profile's own offset, reference removed — because the icon is a 320x126
  silhouette answering "which way does this lean", and a 40 dB span inside 72px
  would flatten every profile into the same squiggle.
*/
(() => {
  'use strict';

  const DATA = {
    /* dbScale was 5.6 (a +/-6.4 dB window). The curves below are real filter
       responses now and Powerful reaches +7.8 dB, so the scale drops to 4.3 —
       35px of headroom above the zero line, the most the box allows. */
    plot: { left: 36, right: 304, top: 22, bottom: 94, zeroY: 57, dbScale: 4.3 },
    bands: [
      { id: 'bass', label: 'BASS', min: 20, max: 250 },
      { id: 'mids', label: 'MIDS', min: 250, max: 4000 },
      { id: 'treble', label: 'TREBLE', min: 4000, max: 10000 },
      { id: 'air', label: 'AIR', min: 10000, max: 20000 }
    ],
    ticks: [20, 1000, 20000],
    boundaries: [250, 4000, 10000],
    profiles: {
      warm: { title: 'Warm', description: 'Bass and lower-mid weight with a gently relaxed upper range.', activeBands: ['bass', 'mids'], points: [[20.0,2.75],[23.2,2.75],[26.8,2.75],[31.1,2.75],[36.0,2.75],[41.7,2.76],[48.3,2.76],[56.0,2.76],[64.8,2.76],[75.1,2.75],[87.0,2.74],[101,2.72],[117,2.67],[135,2.6],[157,2.49],[181,2.34],[210,2.16],[243,1.98],[282,1.79],[326,1.6],[378,1.39],[438,1.15],[507,0.91],[588,0.66],[681,0.44],[788,0.25],[913,0.09],[1058,-0.05],[1225,-0.19],[1419,-0.32],[1644,-0.47],[1904,-0.63],[2206,-0.83],[2555,-1.07],[2960,-1.32],[3428,-1.59],[3971,-1.85],[4600,-2.07],[5328,-2.25],[6172,-2.39],[7149,-2.49],[8280,-2.57],[9591,-2.62],[11110,-2.66],[12869,-2.69],[14906,-2.71],[17266,-2.72],[20000,-2.73]], markerIndices: [1, 2, 4] },
      balanced: { title: 'Balanced', description: 'Small tonal deviations with no frequency region strongly pushed.', activeBands: [], points: [[20.0,1.46],[23.2,1.46],[26.8,1.45],[31.1,1.43],[36.0,1.41],[41.7,1.38],[48.3,1.34],[56.0,1.27],[64.8,1.18],[75.1,1.05],[87.0,0.9],[101,0.72],[117,0.55],[135,0.4],[157,0.28],[181,0.19],[210,0.12],[243,0.08],[282,0.05],[326,0.03],[378,0.02],[438,0.01],[507,0.0],[588,-0.0],[681,-0.0],[788,-0.0],[913,-0.0],[1058,0.0],[1225,0.01],[1419,0.01],[1644,0.02],[1904,0.04],[2206,0.06],[2555,0.09],[2960,0.13],[3428,0.19],[3971,0.26],[4600,0.33],[5328,0.42],[6172,0.5],[7149,0.57],[8280,0.63],[9591,0.67],[11110,0.7],[12869,0.73],[14906,0.74],[17266,0.76],[20000,0.76]], markerIndices: [0, 2, 4] },
      vocal: { title: 'Vocal', description: 'Controlled lows and a broad upper-mid presence rise for forward voices.', activeBands: ['mids'], points: [[20.0,-2.19],[23.2,-2.19],[26.8,-2.18],[31.1,-2.18],[36.0,-2.17],[41.7,-2.15],[48.3,-2.13],[56.0,-2.1],[64.8,-2.06],[75.1,-2.0],[87.0,-1.92],[101,-1.82],[117,-1.7],[135,-1.58],[157,-1.47],[181,-1.37],[210,-1.29],[243,-1.22],[282,-1.16],[326,-1.11],[378,-1.05],[438,-0.98],[507,-0.89],[588,-0.77],[681,-0.62],[788,-0.43],[913,-0.18],[1058,0.13],[1225,0.5],[1419,0.93],[1644,1.35],[1904,1.67],[2206,1.79],[2555,1.65],[2960,1.31],[3428,0.87],[3971,0.42],[4600,0.01],[5328,-0.35],[6172,-0.65],[7149,-0.9],[8280,-1.1],[9591,-1.26],[11110,-1.39],[12869,-1.48],[14906,-1.54],[17266,-1.59],[20000,-1.63]], markerIndices: [1, 3, 4] },
      powerful: { title: 'Powerful', description: 'A strong sub-bass shelf that falls cleanly into the mids.', activeBands: ['bass'], points: [[20.0,7.81],[23.2,7.78],[26.8,7.74],[31.1,7.68],[36.0,7.59],[41.7,7.45],[48.3,7.25],[56.0,6.95],[64.8,6.54],[75.1,6.0],[87.0,5.37],[101,4.7],[117,4.05],[135,3.48],[157,2.99],[181,2.55],[210,2.13],[243,1.71],[282,1.33],[326,1.0],[378,0.73],[438,0.52],[507,0.36],[588,0.23],[681,0.14],[788,0.08],[913,0.02],[1058,-0.01],[1225,-0.04],[1419,-0.06],[1644,-0.08],[1904,-0.09],[2206,-0.1],[2555,-0.1],[2960,-0.11],[3428,-0.11],[3971,-0.12],[4600,-0.12],[5328,-0.12],[6172,-0.12],[7149,-0.12],[8280,-0.12],[9591,-0.12],[11110,-0.12],[12869,-0.12],[14906,-0.12],[17266,-0.12],[20000,-0.12]], markerIndices: [0, 2, 4] },
      spacious: { title: 'Spacious', description: 'Controlled lows with rising upper-treble and air for openness.', activeBands: ['treble', 'air'], points: [[20.0,-1.84],[23.2,-1.84],[26.8,-1.83],[31.1,-1.83],[36.0,-1.83],[41.7,-1.82],[48.3,-1.81],[56.0,-1.79],[64.8,-1.77],[75.1,-1.74],[87.0,-1.7],[101,-1.63],[117,-1.53],[135,-1.41],[157,-1.25],[181,-1.07],[210,-0.9],[243,-0.74],[282,-0.61],[326,-0.51],[378,-0.43],[438,-0.37],[507,-0.32],[588,-0.27],[681,-0.21],[788,-0.15],[913,-0.06],[1058,0.05],[1225,0.19],[1419,0.38],[1644,0.62],[1904,0.92],[2206,1.24],[2555,1.54],[2960,1.75],[3428,1.88],[3971,1.99],[4600,2.14],[5328,2.35],[6172,2.59],[7149,2.83],[8280,3.05],[9591,3.22],[11110,3.35],[12869,3.45],[14906,3.51],[17266,3.56],[20000,3.59]], markerIndices: [0, 2, 4] }
    }
  };

  const { plot: PLOT, bands: BANDS, ticks: TICKS, boundaries: BOUNDARIES, profiles: PROFILES } = DATA;
  let instanceCount = 0;
  const round = (v) => Number(v.toFixed(2));
  const freqToX = (f) => PLOT.left + (Math.log10(f / 20) / Math.log10(20000 / 20)) * (PLOT.right - PLOT.left);
  const dbToY = (db) => PLOT.zeroY - db * PLOT.dbScale;

  function smoothPath(rawPoints, tension = 0.74) {
    const pts = rawPoints.map(([f, db]) => ({ x: freqToX(f), y: dbToY(db) }));
    let d = `M ${round(pts[0].x)} ${round(pts[0].y)}`;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const c1x = p1.x + ((p2.x - p0.x) / 6) * tension;
      const c1y = p1.y + ((p2.y - p0.y) / 6) * tension;
      const c2x = p2.x - ((p3.x - p1.x) / 6) * tension;
      const c2y = p2.y - ((p3.y - p1.y) / 6) * tension;
      d += ` C ${round(c1x)} ${round(c1y)}, ${round(c2x)} ${round(c2y)}, ${round(p2.x)} ${round(p2.y)}`;
    }
    return d;
  }

  function buildGraph(profileKey, uid, compact) {
    const profile = PROFILES[profileKey] || PROFILES.balanced;
    const pathD = smoothPath(profile.points);
    const firstX = freqToX(profile.points[0][0]);
    const lastX = freqToX(profile.points[profile.points.length - 1][0]);
    const areaD = `${pathD} L ${round(lastX)} ${PLOT.zeroY} L ${round(firstX)} ${PLOT.zeroY} Z`;
    const gradientId = `sp-area-${uid}`;
    const clipId = `sp-clip-${uid}`;
    const arrowId = `sp-arrow-${uid}`;

    const bandRects = BANDS.map((band) => {
      const x = freqToX(band.min);
      const width = freqToX(band.max) - x;
      const active = profile.activeBands.includes(band.id) ? ' is-active' : '';
      return `<rect class="sp-band${active}" x="${round(x)}" y="${PLOT.top}" width="${round(width)}" height="${PLOT.bottom - PLOT.top}"/>`;
    }).join('');
    const separators = BOUNDARIES.map((f) => `<line class="sp-separator" x1="${round(freqToX(f))}" y1="${PLOT.top}" x2="${round(freqToX(f))}" y2="${PLOT.bottom}"/>`).join('');
    const markers = compact ? '' : profile.markerIndices.map((pointIndex, markerIndex) => {
      const [f, db] = profile.points[pointIndex];
      return `<circle class="sp-marker" cx="${round(freqToX(f))}" cy="${round(dbToY(db))}" r="2.7" style="--marker-index:${markerIndex}"><title>${f} Hz: ${db > 0 ? '+' : ''}${db.toFixed(1)} dB relative</title></circle>`;
    }).join('');

    const plotBody = `
      <g clip-path="url(#${clipId})">
        ${bandRects}${separators}
        <line class="sp-reference" x1="${PLOT.left}" y1="${PLOT.zeroY}" x2="${PLOT.right}" y2="${PLOT.zeroY}"/>
        <path class="sp-area" style="fill:url(#${gradientId})" d="${areaD}"/>
        <path class="sp-profile-glow" d="${pathD}"/>
        <path class="sp-profile-line" pathLength="1" d="${pathD}"/>
        ${markers}
      </g>`;

    const defs = `
      <defs>
        <linearGradient id="${gradientId}" x1="0" y1="${PLOT.top}" x2="0" y2="${PLOT.bottom}" gradientUnits="userSpaceOnUse"><stop stop-color="var(--sp-curve)" stop-opacity=".24"/><stop offset="1" stop-color="var(--sp-curve)" stop-opacity="0"/></linearGradient>
        <clipPath id="${clipId}"><rect x="${PLOT.left}" y="${PLOT.top}" width="${PLOT.right - PLOT.left}" height="${PLOT.bottom - PLOT.top}" rx="4"/></clipPath>
        ${compact ? '' : `<marker id="${arrowId}" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 8 4 L 0 8 Z" fill="currentColor" opacity=".55"/></marker>`}
      </defs>`;

    /* compact: the plot only, on a tight viewBox — band labels and axis captions
       are unreadable at list-row scale, so they are not rendered at all */
    if (compact) {
      return `<svg class="sp-svg" viewBox="${PLOT.left} ${PLOT.top} ${PLOT.right - PLOT.left} ${PLOT.bottom - PLOT.top}" fill="none" role="img" aria-label="${profile.title} tonal balance. ${profile.description}">${defs}${plotBody}</svg>`;
    }

    const bandLabels = BANDS.map((band) => `<text class="sp-band-label" x="${round((freqToX(band.min) + freqToX(band.max)) / 2)}" y="15" text-anchor="middle">${band.label}</text>`).join('');
    const tickLabels = TICKS.map((f) => `<text class="sp-axis-label" x="${round(freqToX(f))}" y="109" text-anchor="middle">${f === 20 ? '20 Hz' : f === 1000 ? '1 kHz' : '20 kHz'}</text>`).join('');

    return `<svg class="sp-svg" viewBox="0 0 320 126" fill="none" role="img" aria-label="${profile.title} tonal balance. ${profile.description}">
      ${defs}
      <rect class="sp-frame" x=".5" y=".5" width="319" height="125" rx="14"/>
      ${bandLabels}
      ${plotBody}
      <line class="sp-axis sp-axis--x" x1="${PLOT.left}" y1="${PLOT.bottom}" x2="${PLOT.right + 1}" y2="${PLOT.bottom}" marker-end="url(#${arrowId})"/>
      <line class="sp-axis sp-axis--y" x1="${PLOT.left}" y1="${PLOT.bottom}" x2="${PLOT.left}" y2="${PLOT.top - 1}" marker-end="url(#${arrowId})"/>
      ${tickLabels}
      <text class="sp-axis-caption" x="13" y="58" text-anchor="middle" transform="rotate(-90 13 58)">LEVEL</text>
      <text class="sp-axis-caption" x="304" y="120" text-anchor="end">FREQUENCY</text>
    </svg>`;
  }

  class SoundProfileIcon extends HTMLElement {
    static get observedAttributes() { return ['profile', 'frame', 'animated', 'compact', 'decorative']; }

    constructor() {
      super();
      this._uid = `sp${++instanceCount}`;
      this._observer = null;
    }

    connectedCallback() {
      this.render();
      this.setupAnimation();
      /* a decorative icon lives inside another control — never focusable, and it
         must not swallow that control's clicks */
      if (this._decorative()) return;
      if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
      this.addEventListener('click', this.replay);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      this.removeEventListener('click', this.replay);
      this.removeEventListener('keydown', this.onKeydown);
    }

    attributeChangedCallback() {
      if (this.isConnected) {
        this.render();
        this.setupAnimation();
      }
    }

    _decorative() { return this.hasAttribute('decorative') || this.hasAttribute('compact'); }

    render() {
      const requested = (this.getAttribute('profile') || 'balanced').toLowerCase();
      const key = PROFILES[requested] ? requested : 'balanced';
      const profile = PROFILES[key];
      const compact = this.hasAttribute('compact');
      this.dataset.profile = key;
      this.classList.toggle('sp-no-frame', this.getAttribute('frame') === 'false' || compact);
      this.classList.toggle('sp-static', this.getAttribute('animated') === 'false');
      this.classList.toggle('sp-compact', compact);
      if (this._decorative()) {
        /* the row it sits in carries the accessible name */
        this.setAttribute('aria-hidden', 'true');
        this.removeAttribute('role');
      } else {
        this.setAttribute('role', 'group');
        this.setAttribute('aria-label', `${profile.title} tonal balance`);
      }
      this.innerHTML = buildGraph(key, this._uid, compact);
    }

    setupAnimation() {
      if (this._observer) this._observer.disconnect();
      if (this.classList.contains('sp-static') || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        this.classList.add('is-active');
        return;
      }
      this.classList.remove('is-active');
      if (!('IntersectionObserver' in window)) { this.classList.add('is-active'); return; }
      this._observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.replay();
          this._observer.disconnect();
        }
      }, { threshold: 0.3 });
      this._observer.observe(this);
    }

    replay = () => {
      if (this.classList.contains('sp-static')) return;
      this.classList.remove('is-active');
      void this.offsetWidth;
      this.classList.add('is-active');
      this.dispatchEvent(new CustomEvent('sound-profile-replay', { bubbles: true, detail: { profile: this.dataset.profile } }));
    };

    onKeydown = (event) => {
      if (this._decorative()) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.replay();
      }
    };
  }

  if (!customElements.get('sound-profile-icon')) customElements.define('sound-profile-icon', SoundProfileIcon);

  window.KiwiSoundProfileIcons = {
    profiles: Object.keys(PROFILES),
    data: DATA,
    replayAll() { document.querySelectorAll('sound-profile-icon').forEach((i) => i.replay && i.replay()); }
  };
})();
